import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api } from "../api";

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseFloat(n) || 0);
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ─── Admin Login ───────────────────────────────────────────────────────────────
export function AdminLoginPage() {
  const { adminLogin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await api.adminLogin(form);
      adminLogin(data.token, { email: data.email, name: data.name, roles: data.roles });
      if (data.must_change_password) {
        navigate("/admin/change-password");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-panel-left">
        <div className="auth-brand">
          <img src="/logo.jpg" alt="Cow Town Redi Mix" />
          <div className="auth-brand-name">Admin Portal</div>
          <div className="auth-brand-tagline">CowTown Redi Mix — Staff Access Only</div>
        </div>
      </div>
      <div className="auth-panel-right">
        <div className="auth-form-wrap">
          <div className="auth-form-title">Staff Login</div>
          <div className="auth-form-sub">Sign in with your Cowtown admin credentials.</div>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input className="form-input" type="email" required
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" required
                value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="auth-switch" style={{ marginTop: 16 }}>
            <Link to="/login">← Customer portal</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Admin Dashboard ───────────────────────────────────────────────────────────
export function AdminDashboardPage() {
  const { adminUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    api.adminDashboard()
      .then(setStats)
      .catch(err => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div>
      {loadError && <div className="alert alert-error">{loadError}</div>}
      <div className="page-header">
        <div className="page-title">Welcome, {adminUser?.name}</div>
        <div className="page-subtitle">
          {stats?.last_sync
            ? `Last QB sync: ${fmtDateTime(stats.last_sync.completed_at)} — ${stats.last_sync.status}`
            : "No sync data available"}
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-card">
          <div className={`stat-number ${stats?.pending_disputes > 0 ? "red" : ""}`}>
            {stats?.pending_disputes || 0}
          </div>
          <div className="stat-label">Pending Disputes</div>
          {stats?.pending_disputes > 0 && (
            <Link to="/admin/disputes" className="btn btn-sm btn-danger" style={{ marginTop: 12 }}>
              Review Now
            </Link>
          )}
        </div>
        <div className="stat-card">
          <div className={`stat-number ${stats?.pending_ticket_requests > 0 ? "red" : ""}`}>
            {stats?.pending_ticket_requests || 0}
          </div>
          <div className="stat-label">Ticket Requests</div>
          {stats?.pending_ticket_requests > 0 && (
            <Link to="/admin/tickets" className="btn btn-sm btn-primary" style={{ marginTop: 12 }}>
              View Requests
            </Link>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats?.active_portal_users || 0}</div>
          <div className="stat-label">Active Portal Users</div>
          <Link to="/admin/customers" className="btn btn-sm btn-outline" style={{ marginTop: 12 }}>
            Manage
          </Link>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Quick Actions</span></div>
          <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Link to="/admin/customers" className="btn btn-outline" style={{ justifyContent: "flex-start" }}>
              ◯ Search Customer Accounts
            </Link>
            <Link to="/admin/disputes" className="btn btn-outline" style={{ justifyContent: "flex-start" }}>
              ⚑ Review Disputes
            </Link>
            <Link to="/admin/tickets" className="btn btn-outline" style={{ justifyContent: "flex-start" }}>
              ◻ Fulfill Ticket Requests
            </Link>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">Your Role</span></div>
          <div className="card-body">
            {(() => {
              // roles[] is authoritative; the `role` column is a derived mirror
              // and is deliberately not consulted here.
              const roles = Array.isArray(adminUser?.roles) ? adminUser.roles : [];
              const labelMap = { super_admin: "Super Admin", admin: "Admin", billing: "Billing", qc: "QC" };
              const clsMap   = { super_admin: "super", billing: "billing", qc: "qc" };
              // A super admin also carries the base "admin" role; show the one
              // badge that actually describes them rather than both.
              const shown = roles.includes("super_admin")
                ? roles.filter(r => r !== "admin")
                : roles;

              // An account with no roles is misconfigured, not merely limited.
              // Say so outright instead of rendering "logged in as X with  access".
              if (roles.length === 0) {
                return (
                  <>
                    <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 12 }}>
                      You are logged in as <strong>{adminUser?.name}</strong>, but your account
                      has <strong>no roles assigned</strong>.
                    </p>
                    <div className="alert alert-error" style={{ fontSize: "0.83rem", marginBottom: 0 }}>
                      Most admin actions will be refused until this is fixed. Ask a super admin to
                      open Super Admin → Admins, edit your account, and re-save its role.
                    </div>
                  </>
                );
              }

              return (
                <>
                  <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 12 }}>
                    You are logged in as <strong>{adminUser?.name}</strong> with{" "}
                    {shown.map(r => (
                      <span key={r} className={`admin-badge ${clsMap[r] || ""}`} style={{ marginRight: 4 }}>
                        {labelMap[r] || r}
                      </span>
                    ))}
                    {" "}access.
                  </p>
                  {roles.includes("super_admin") && (
                    <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Full access to all modules and admin management.</p>
                  )}
                  {roles.includes("admin") && !roles.includes("super_admin") && (
                    <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Full access: customer management, disputes, ticket requests, impersonation.</p>
                  )}
                  {roles.includes("billing") && (
                    <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Access: customer accounts, ticket requests, uploading tickets.</p>
                  )}
                  {roles.includes("qc") && (
                    <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Access: disputes — review and add internal notes.</p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Admin Customers ───────────────────────────────────────────────────────────
export function AdminCustomersPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [showInactive, setShowInactive] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 25, search, show_inactive: showInactive });
    api.adminGetCustomers(params.toString())
      .then(d => { setData(d); setLoadError(""); })
      .catch(err => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [page, search, showInactive]);

  useEffect(() => { load(); }, [load]);

  function handleSearchChange(e) {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Customer Accounts</div>
        <div className="page-subtitle">Manage portal access, verify emails, reset passwords.</div>
      </div>

      {loadError && <div className="alert alert-error">{loadError}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          className="form-input"
          placeholder="Search by company name or email..."
          value={searchInput}
          onChange={handleSearchChange}
          style={{ maxWidth: 400 }}
          autoFocus
        />
        {searchInput && (
          <button type="button" className="btn btn-outline" onClick={() => { setSearch(""); setSearchInput(""); setPage(1); }}>Clear</button>
        )}
        <button
          type="button"
          className={"btn btn-sm " + (showInactive ? "btn-primary" : "btn-outline")}
          onClick={() => { setShowInactive(s => !s); setPage(1); }}
          style={{ marginLeft: "auto" }}
        >
          {showInactive ? "Hide Inactive" : "Show Inactive"}
        </button>
      </div>

      <div className="card">
        {loading
          ? <div className="loading">Loading customers…</div>
          : data?.customers?.length === 0
            ? <div className="empty-state"><p>No customers found.</p></div>
            : (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Email</th>
                        <th>Portal Account</th>
                        <th>Last Login</th>
                        <th className="td-right">Balance</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.customers.map(c => (
                        <tr key={c.customer_id || c.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{c.customer_name || c.company_name}</div>
                            {c.customer_name && c.company_name !== c.customer_name && (
                              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{c.company_name}</div>
                            )}
                          </td>
                          <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                            {c.email || c.qb_email || "—"}
                          </td>
                          <td>
                            <span className={`badge ${c.has_account ? "badge-paid" : "badge-partial"}`}>
                              {c.has_account ? "Registered" : "Not Registered"}
                            </span>
                          </td>

                          <td style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>
                            {c.last_login_at ? fmtDate(c.last_login_at) : c.has_account ? "Never" : "—"}
                          </td>
                          <td className="td-right td-mono" style={{ color: parseFloat(c.total_balance) > 0 ? "var(--red)" : "var(--text-muted)" }}>
                            {fmt(c.total_balance)}
                          </td>
                          <td style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            {!c.qb_active && <span className="badge badge-overdue" style={{ fontSize: "0.6rem" }}>Inactive</span>}
                            <Link to={`/admin/customers/${c.customer_id}`} className="btn btn-outline btn-sm">
                              {c.has_account ? "Manage" : "View"}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.pagination?.pages > 1 && (
                  <div className="pagination">
                    <span>Page {data.pagination.page} of {data.pagination.pages} · {data.pagination.total} accounts</span>
                    <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                    <button className="btn btn-outline btn-sm" disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
                  </div>
                )}
              </>
            )
        }
      </div>
    </div>
  );
}

// ─── Admin Customer Detail ─────────────────────────────────────────────────────
export function AdminCustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminUser } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  useEffect(() => {
    api.adminGetCustomer(id)
      .then(setCustomer)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    loadInvites();
  }, [id]);

  function loadInvites() {
    setInvitesLoading(true);
    api.adminGetCustomerInvites(id)
      .then(setInvites)
      .catch(() => {})
      .finally(() => setInvitesLoading(false));
  }

  async function handleToggleActive() {
    try {
      const result = await api.adminToggleActive(id);
      if (result.deleted) {
        setCustomer(c => ({ ...c, is_active: false, has_account: false, email: null }));
        setMsg("Portal access removed. Customer can be re-invited.");
      } else {
        setCustomer(c => ({ ...c, is_active: result.is_active }));
        setMsg(`Account ${result.is_active ? "activated" : "deactivated"}.`);
      }
    } catch (err) { setError(err.message); }
  }

  async function handlePreview() {
    try {
      const data = await api.adminPreview(customer.customer_id);
      // Store preview token and open in new tab
      localStorage.setItem("ar_token", data.token);
      localStorage.setItem("ar_email", data.email);
      localStorage.setItem("ar_preview_mode", "true");
      localStorage.setItem("ar_preview_customer", data.customer_name);
      window.open("/", "_blank");
    } catch (err) { setError(err.message); }
  }

  async function handleSendInvite(e) {
    e.preventDefault();
    setInviteSending(true);
    try {
      await api.adminSendInvite(customer.customer_id, { email: inviteEmail });
      setMsg("Invite sent to " + inviteEmail + ".");
      setInviteEmail("");
      loadInvites();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviteSending(false);
    }
  }

  async function handleRevokeInvite(inviteId) {
    try {
      await api.adminCancelInvite(inviteId);
      setMsg("Invite revoked.");
      loadInvites();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleResendInvite(inviteId, email) {
    try {
      await api.adminResendInvite(inviteId);
      setMsg("Invite resent to " + email + ".");
      loadInvites();
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) return <div className="loading">Loading…</div>;
  if (error && !customer) return <div className="alert alert-error">{error}</div>;

  return (
    <div>
      <div className="page-header">
        <button className="btn-link" style={{ fontSize: "0.85rem", marginBottom: 8 }} onClick={() => navigate(-1)}>
          ← Back to customers
        </button>
        <div className="page-title">{customer?.customer_name || customer?.company_name}</div>
        <div className="page-subtitle">{customer?.email}</div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Account Info */}
        <div className="card">
          <div className="card-header"><span className="card-title">Account Information</span></div>
          <div className="card-body">
            {[
              ["Customer Name", customer?.customer_name || "—"],
              ["Company Name", customer?.company_name || "—"],
              ["Email", customer?.email],
              ["Account Active", customer?.is_active ? "✓ Yes" : "✗ No"],
              ["Total Balance", fmt(customer?.total_balance)],
              ["Open Invoices", customer?.open_invoice_count],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: "0.875rem" }}>
                <span style={{ color: "var(--text-muted)" }}>{label}</span>
                <span style={{ fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Reset password */}
          {customer?.has_account && (
            <div className="card">
              <div className="card-header"><span className="card-title">Reset Password</span></div>
              <div className="card-body">
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 12 }}>
                  Send the customer an email with a link to reset their own password.
                </p>
                <button className="btn btn-outline" onClick={async () => {
                  try {
                    await api.adminResetPassword(customer.customer_id, {});
                    setMsg("Password reset email sent to " + customer.email + ".");
                  } catch (err) { setError(err.message); }
                }}>
                  ✉ Send Reset Email
                </button>
              </div>
            </div>
          )}
		  
		  {/* Remove portal access */}
          {customer?.has_account && (
            <div className="card">
              <div className="card-header"><span className="card-title">Portal Access</span></div>
              <div className="card-body">
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 12 }}>
                  Remove this customer's portal access. They can be re-invited at any time.
                </p>
                <button className="btn btn-danger" onClick={handleToggleActive}>
                  Remove Portal Access
                </button>
              </div>
            </div>
          )}

          {/* Impersonate */}
          {(adminUser?.roles || []).some(r => ["admin","super_admin"].includes(r)) && (
            <div className="card">
              <div className="card-header"><span className="card-title">Preview Customer View</span></div>
              <div className="card-body">
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 12 }}>
                  Open a preview of this customer's portal view in a new tab.
                </p>
                <button className="btn btn-outline" onClick={handlePreview}>
                  ◉ Preview as Customer
                </button>
              </div>
            </div>
          )}

          {/* Send Invite + Log */}
          <div className="card">
            <div className="card-header"><span className="card-title">Portal Invites</span></div>
            <div className="card-body">
              {!customer?.has_account && (
                <>
                  <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 12 }}>
                    Send an invite link for this customer to set up their portal account.
                  </p>
                  <form onSubmit={handleSendInvite} style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                    <input className="form-input" type="email" placeholder="customer@email.com"
                      value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required
                      style={{ flex: 1 }} />
                    <button type="submit" className="btn btn-primary" disabled={inviteSending}>
                      {inviteSending ? "Sending..." : "✉ Send Invite"}
                    </button>
                  </form>
                </>
              )}
              {customer?.has_account && (
                <p style={{ fontSize: "0.875rem", color: "var(--paid)", marginBottom: 16, fontWeight: 500 }}>
                  ✓ This customer has an active portal account.
                </p>
              )}

              {/* Invite log */}
              {invites.length > 0 && (
                <div>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>
                    Invite History
                  </div>
                  {invites.map(inv => (
                    <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: "0.85rem" }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{inv.email}</div>
                        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                          Sent {new Date(inv.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                          {inv.accepted_at && <span style={{ color: "var(--paid)", marginLeft: 8 }}>✓ Accepted</span>}
                        </div>
                      </div>
                      {!inv.accepted_at && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => handleResendInvite(inv.id, inv.email)}>
                            Resend
                          </button>
                          <button className="btn btn-sm" style={{ background: "#f8d7da", color: "#842029", border: "1px solid #f5c2c7", borderRadius: "var(--radius)", padding: "4px 10px", cursor: "pointer", fontSize: "0.8rem" }}
                            onClick={() => handleRevokeInvite(inv.id)}>
                            Revoke
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!invitesLoading && invites.length === 0 && (
                <div style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>No invites sent yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Admin Disputes ────────────────────────────────────────────────────────────
export function AdminDisputesPage() {
  const { adminUser } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.adminGetDisputes(statusFilter)
      .then(d => { setDisputes(d); setLoadError(""); })
      .catch(err => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdate(newStatus) {
    setSaving(true);
    try {
      await api.adminUpdateDispute(selected.id, {
        ...(newStatus ? { status: newStatus } : {}),
        internal_notes: notes,
        customer_note: customerNote || undefined,
      });
      setMsg("Dispute updated.");
      setSelected(null);
      load();
    } catch (err) {
      setMsg("Error: " + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Disputes</div>
        <div className="page-subtitle">Review and respond to customer invoice disputes.</div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {loadError && <div className="alert alert-error">{loadError}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["pending", "reviewing", "resolved"].map(s => (
          <button key={s} className={`btn btn-sm ${statusFilter === s ? "btn-primary" : "btn-outline"}`}
            onClick={() => { setStatusFilter(s); setSelected(null); }}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 1fr" : "1fr", gap: 20 }}>
        <div className="card">
          {loading
            ? <div className="loading">Loading…</div>
            : disputes.length === 0
              ? <div className="empty-state"><p>No {statusFilter} disputes.</p></div>
              : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Invoice #</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {disputes.map(d => (
                        <tr key={d.id} style={{ background: selected?.id === d.id ? "var(--blue-lt)" : undefined }}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{d.customer_name || d.company_name}</div>
                            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{d.customer_email}</div>
                          </td>
                          <td>{d.invoice_number}</td>
                          <td style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>{fmtDate(d.created_at)}</td>
                          <td><span className={`badge badge-${d.status}`}>{d.status}</span></td>
                          <td>
                            <button className="btn btn-outline btn-sm" onClick={() => {
                              setSelected(d);
                              setNotes(d.internal_notes || "");
                              setCustomerNote(d.customer_note || "");
                            }}>Review</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
          }
        </div>

        {selected && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Invoice #{selected.invoice_number}</span>
              <button className="btn-link" onClick={() => setSelected(null)}>✕ Close</button>
            </div>
            <div className="card-body">
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 4 }}>Customer</div>
                <div style={{ fontWeight: 500 }}>{selected.customer_name || selected.company_name}</div>
                <div style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>{selected.customer_email}</div>
              </div>

              <div style={{ marginBottom: 16, padding: "12px 14px", background: "var(--bg)", borderRadius: "var(--radius)", fontSize: "0.875rem" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 6 }}>Dispute Description</div>
                {selected.description}
              </div>

              <div className="form-group">
                <label className="form-label">Internal Notes (not visible to customer)</label>
                <textarea className="form-textarea" rows={3}
                  value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Add notes for internal reference..." />
              </div>

              {(adminUser?.roles || []).some(r => ["admin","super_admin"].includes(r)) && (
                <div className="form-group">
                  <label className="form-label">Note to Customer (sent on resolution)</label>
                  <textarea className="form-textarea" rows={3}
                    value={customerNote} onChange={e => setCustomerNote(e.target.value)}
                    placeholder="Optional message to the customer..." />
                </div>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {selected.status !== "reviewing" && (
                  <button className="btn btn-outline" disabled={saving} onClick={() => handleUpdate("reviewing")}>
                    Mark as Reviewing
                  </button>
                )}
                <button className="btn btn-primary" disabled={saving} onClick={() => handleUpdate(null)}>
                  Save Notes
                </button>
                {(adminUser?.roles || []).some(r => ["admin","super_admin"].includes(r)) && (
                  <button className="btn btn-danger" disabled={saving} onClick={() => handleUpdate("resolved")}>
                    {saving ? "Saving…" : "Resolve Dispute"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin Ticket Requests ─────────────────────────────────────────────────────
export function AdminTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.adminGetTicketRequests(statusFilter)
      .then(t => { setTickets(t); setLoadError(""); })
      .catch(err => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleUploadAndFulfill() {
    if (!file) { alert("Please select a file first."); return; }
    setUploading(true);
    try {
      // Read file as base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Upload file
      await api.adminUploadTicket(selected.id, {
        filename: file.name,
        file_data: base64,
        invoice_id: selected.invoice_id,
      });

      // Fulfill request
      await api.adminFulfillTicket(selected.id);

      setMsg(`Tickets uploaded and request fulfilled. Customer has been notified.`);
      setSelected(null);
      setFile(null);
      load();
    } catch (err) {
      setMsg("Error: " + err.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Ticket Requests</div>
        <div className="page-subtitle">Upload delivery tickets for customer invoice requests.</div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {loadError && <div className="alert alert-error">{loadError}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["pending", "fulfilled"].map(s => (
          <button key={s} className={`btn btn-sm ${statusFilter === s ? "btn-primary" : "btn-outline"}`}
            onClick={() => { setStatusFilter(s); setSelected(null); }}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 1fr" : "1fr", gap: 20 }}>
        <div className="card">
          {loading
            ? <div className="loading">Loading…</div>
            : tickets.length === 0
              ? <div className="empty-state"><p>No {statusFilter} ticket requests.</p></div>
              : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Invoice #</th>
                        <th>Invoice Date</th>
                        <th>Requested</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map(t => (
                        <tr key={t.id} style={{ background: selected?.id === t.id ? "var(--blue-lt)" : undefined }}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{t.customer_name || t.company_name}</div>
                            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{t.customer_email}</div>
                          </td>
                          <td>{t.invoice_number}</td>
                          <td style={{ fontSize: "0.83rem" }}>{fmtDate(t.invoice_date)}</td>
                          <td style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>{fmtDate(t.created_at)}</td>
                          <td><span className={`badge badge-${t.status}`}>{t.status}</span></td>
                          <td>
                            {t.status === "pending" && (
                              <button className="btn btn-outline btn-sm" onClick={() => setSelected(t)}>
                                Fulfill
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
          }
        </div>

        {selected && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Fulfill Ticket Request</span>
              <button className="btn-link" onClick={() => { setSelected(null); setFile(null); }}>✕ Close</button>
            </div>
            <div className="card-body">
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 4 }}>Customer</div>
                <div style={{ fontWeight: 500 }}>{selected.customer_name || selected.company_name}</div>
                <div style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>{selected.customer_email}</div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 4 }}>Invoice</div>
                <div style={{ fontWeight: 500 }}>#{selected.invoice_number} — {fmtDate(selected.invoice_date)}</div>
                <div style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>{fmt(selected.amount)}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Upload Tickets (PDF)</label>
                <input
                  type="file"
                  accept=".pdf,.PDF"
                  onChange={e => setFile(e.target.files[0])}
                  style={{ fontSize: "0.875rem" }}
                />
                {file && <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: 6 }}>Selected: {file.name}</div>}
              </div>

              <div className="alert alert-info" style={{ fontSize: "0.83rem" }}>
                Uploading will mark this request as fulfilled and email the customer automatically.
              </div>

              <button className="btn btn-primary" disabled={!file || uploading} onClick={handleUploadAndFulfill}>
                {uploading ? "Uploading…" : "Upload & Notify Customer"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
